import { Button } from "@/components/ui/button";
import { Twitter, Linkedin, Facebook, Link2, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
}

export function ShareButtons({ url, title, description }: ShareButtonsProps) {
  const { toast } = useToast();
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || "");

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied!",
        description: "The article link has been copied to your clipboard.",
      });
    } catch {
      toast({
        title: "Failed to copy",
        description: "Please copy the URL manually from the address bar.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-muted-foreground">Share this article</span>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          asChild
          className="gap-2 hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2] hover:border-[#1DA1F2]/30"
        >
          <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter">
            <Twitter className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Twitter</span>
          </a>
        </Button>
        <Button
          variant="outline"
          size="sm"
          asChild
          className="gap-2 hover:bg-[#0A66C2]/10 hover:text-[#0A66C2] hover:border-[#0A66C2]/30"
        >
          <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn">
            <Linkedin className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">LinkedIn</span>
          </a>
        </Button>
        <Button
          variant="outline"
          size="sm"
          asChild
          className="gap-2 hover:bg-[#1877F2]/10 hover:text-[#1877F2] hover:border-[#1877F2]/30"
        >
          <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
            <Facebook className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Facebook</span>
          </a>
        </Button>
        <Button
          variant="outline"
          size="sm"
          asChild
          className="gap-2 hover:bg-primary/10 hover:text-primary hover:border-primary/30"
        >
          <a href={shareLinks.email} aria-label="Share via Email">
            <Mail className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Email</span>
          </a>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={copyToClipboard}
          className="gap-2 hover:bg-primary/10 hover:text-primary hover:border-primary/30"
          aria-label="Copy link to clipboard"
        >
          <Link2 className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Copy Link</span>
        </Button>
      </div>
    </div>
  );
}